import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Fade,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Api from "../../../../services/Api";
import Notifications from "../../../../services/Notifications";

const ADDRESS_TYPE_VALUES = {
  BILL_TO: "0",
  SHIP_TO: "1",
};

export const AddressTypeToggle = ({ localized, addressType }) => {
  const queryClient = useQueryClient();
  const [currentGlobalType, setCurrentGlobalType] = useState(addressType);

  useEffect(() => {
    setCurrentGlobalType(addressType);
  }, [addressType]);

  const updateGlobalTypeMutation = useMutation({
    mutationFn: async (newType) => {
      const response = await Api.fetch(
        "/api/Administrative/updateDomesticCountryAddressType",
        {
          method: "POST",
          body: JSON.stringify({
            addressType: newType,
          }),
        }
      );

      if (response) {
        return response.json();
      }

      return response;
    },
    onMutate: async (newType) => {
      setCurrentGlobalType(newType);

      await queryClient.cancelQueries({ queryKey: ["domesticCountries"] });

      const previousData = queryClient.getQueryData(["domesticCountries"]);

      if (previousData) {
        queryClient.setQueryData(["domesticCountries"], {
          ...previousData,
          addressType: newType,
        });
      }

      return { previousData };
    },
    onSuccess: (response) => {
      if (response && response.isSuccess) {
        Notifications.message(localized.AddressTypeUpdated);

        queryClient.invalidateQueries({
          queryKey: ["domesticCountries"],
        });
      } else {
        Notifications.error(localized.AddressTypeUpdateFailed);
      }
    },
    onError: (error, newType, context) => {
      console.error("Error saving address type setting:", error);

      setCurrentGlobalType(addressType);

      if (context?.previousData) {
        queryClient.setQueryData(["domesticCountries"], context.previousData);
      }

      Notifications.error(localized.AddressTypeUpdateFailed);
    },
  });

  const handleGlobalTypeChange = (event) => {
    const newType = event.target.value;

    updateGlobalTypeMutation.mutate(newType);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Typography
        variant="body1"
        sx={{ marginRight: 2, color: "white", fontWeight: "bold" }}
      >
        {localized.AddressType}:
      </Typography>

      <Box sx={{ minHeight: 40, display: "flex", alignItems: "center" }}>
        <Fade in={updateGlobalTypeMutation.isPending} timeout={300}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CircularProgress size={24} sx={{ color: "white" }} />
          </Box>
        </Fade>

        <Fade in={!updateGlobalTypeMutation.isPending} timeout={300}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControl component="fieldset" size="medium">
              <RadioGroup
                name="globalType"
                value={currentGlobalType}
                onChange={handleGlobalTypeChange}
                row
              >
                <FormControlLabel
                  value={ADDRESS_TYPE_VALUES.BILL_TO}
                  control={<Radio size="medium" sx={{ color: "white" }} />}
                  label={localized.BillTo || "Bill To"}
                  sx={{ color: "white", marginRight: 2, fontSize: "1.1rem" }}
                />

                <FormControlLabel
                  value={ADDRESS_TYPE_VALUES.SHIP_TO}
                  control={<Radio size="medium" sx={{ color: "white" }} />}
                  label={localized.ShipTo || "Ship To"}
                  sx={{ color: "white", fontSize: "1.1rem" }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};
